import { situationOf } from '../coach';
import type { CallView } from '../game';
import { SLOT_POSITION } from '../playdef';
import type { Side } from '../subs';
import type {
  Combatant,
  DefensePlayDef,
  Game,
  OffensePlayDef,
  OffenseSlot,
  DefenseSlot,
} from '../types';
import { opponentOf, sideWinChance, type BoardKind } from './board';
import { lineups, margin, noise, teamOn, type AiContext } from './context';

/**
 * Play calls (slice 7): the situational table (balance.json `coach.situations`, as the scripted
 * coach reads it), each play's weight shifted by how its key matchups stand on the board.
 *
 *   edge  = mean over the slots whose position the play leans on of (win chance - 0.5)
 *   score = ln(table weight) + coach.ai.matchupWeight x edge + risk + noise
 *   pick  = one draw, weighted by exp(score)
 */

type AnyPlay = OffensePlayDef | DefensePlayDef;
type Slot = OffenseSlot | DefenseSlot;

/**
 * The battle a slot fights in a play, from its role: a blocker blocks; a route runner is covered
 * (a screen's carry route is tackled); the carrier is tackled; a passer throws; a rusher or
 * blitzer is blocked; a man or zone defender covers. `battles` is false for the passer's throw and
 * the hand-off: neither is a battle, so a move spent there is never used.
 */
export function slotKind(
  play: AnyPlay,
  slot: Slot,
  b: AiContext['b'],
): { kind: BoardKind; battles: boolean } | null {
  if (play.side === 'offense') {
    const a = play.assignments[slot as OffenseSlot];
    switch (a.role) {
      case 'block':
        return { kind: 'block', battles: true };
      case 'route': {
        const route = (b.routes as Record<string, { carry?: boolean } | undefined>)[a.route ?? ''];
        return { kind: route?.carry ? 'tackle' : 'coverage', battles: true };
      }
      case 'carry':
        return { kind: 'tackle', battles: true };
      case 'pass':
        return { kind: 'throw', battles: false };
      default:
        return null;
    }
  }
  const role = play.assignments[slot as DefenseSlot].role;
  return { kind: role === 'rush' || role === 'blitz' ? 'block' : 'coverage', battles: true };
}

/** The slots whose position the play leans on (its `leansOn`), in slot order. */
export function leanedSlots(play: AnyPlay): readonly Slot[] {
  let slots = LEANED.get(play);
  if (!slots) {
    slots = Object.keys(play.assignments).filter((s) =>
      play.leansOn.includes(SLOT_POSITION[s as Slot]),
    ) as Slot[];
    LEANED.set(play, slots);
  }
  return slots;
}
const LEANED = new WeakMap<AnyPlay, Slot[]>();

/** The play's edge on the board: the mean of (win chance - 0.5) over its leaned-on slots. */
export function edgeOf(
  play: AnyPlay,
  side: Side,
  ours: Record<string, Combatant>,
  theirs: Record<string, Combatant>,
  b: AiContext['b'],
  /** Win chances already worked out this call, by slot and kind (plays share their battles). */
  seen: Map<string, number> = new Map(),
): number {
  let sum = 0;
  let n = 0;
  for (const s of leanedSlots(play)) {
    const k = slotKind(play, s, b);
    if (!k) continue;
    const key = `${s}:${k.kind}`;
    let p = seen.get(key);
    if (p === undefined) {
      p = sideWinChance(side, k.kind, ours[s] as Combatant, theirs[opponentOf(s)] as Combatant, b);
      seen.set(key, p);
    }
    sum += p - 0.5;
    n += 1;
  }
  return n > 0 ? sum / n : 0;
}

/**
 * Game horizon only: the push toward risk (+) or safety (-) from score and time, per risk step.
 * Trailing by coach.ai.riskBias.trailingBy or more in the last quarter or overtime pushes up;
 * leading in the last quarter's last leadingSeconds pushes down.
 */
export function riskBias(game: Readonly<Game>, side: Side, ctx: AiContext): number {
  if (ctx.horizon !== 'game') return 0;
  const r = ctx.b.coach.ai.riskBias;
  const late = game.quarter >= ctx.b.football.quarters;
  if (!late) return 0;
  const m = margin(game, teamOn(game, side));
  if (m <= -r.trailingBy) return r.trailing;
  const regulation = game.quarter === ctx.b.football.quarters;
  if (m > 0 && regulation && game.clock <= r.leadingSeconds) return -r.leading;
  return 0;
}

const RISK_STEP = { low: -1, medium: 0, high: 1 } as const;

export interface PlayScore {
  id: string;
  /** The situational table's weight. */
  weight: number;
  edge: number;
  risk: number;
  /** ln(weight) + matchupWeight x edge + risk, before noise. */
  score: number;
}

/** Every play the situation row weights, scored (no noise, no draws). `game` defaults to the view's. */
export function scorePlays(
  view: CallView,
  side: Side,
  ctx: AiContext,
  game: Readonly<Game> = view.game,
  sides: ReturnType<typeof lineups> = lineups(game, side),
): PlayScore[] {
  const row = situationOf({ game, kind: view.kind }, ctx.b);
  const weights = row[side];
  const book: readonly AnyPlay[] = view.playbook[side];
  const weighted = book.filter((p) => (weights[p.id] ?? 0) > 0);
  const plays = weighted.length > 0 ? weighted : book;
  const { ours, theirs } = sides;
  const bias = riskBias(game, side, ctx);
  const seen = new Map<string, number>();
  return plays.map((p) => {
    const weight = weighted.length > 0 ? (weights[p.id] as number) : 1;
    const edge = edgeOf(p, side, ours, theirs, ctx.b, seen);
    const risk = bias * RISK_STEP[p.risk];
    return {
      id: p.id,
      weight,
      edge,
      risk,
      score: Math.log(weight) + ctx.b.coach.ai.matchupWeight * edge + risk,
    };
  });
}

/** The call: each play's score plus the level's noise, then one weighted draw over exp(score). */
export function callPlay(
  view: CallView,
  side: Side,
  ctx: AiContext,
  game: Readonly<Game> = view.game,
  sides: ReturnType<typeof lineups> = lineups(game, side),
): string {
  const scores = scorePlays(view, side, ctx, game, sides);
  if (scores.length === 0) throw new Error('the playbook is empty');
  const noisy = scores.map((s) => s.score + noise(ctx, ctx.b.coach.ai.jitterScale.call));
  const top = Math.max(...noisy);
  const w = noisy.map((x) => Math.exp(x - top));
  const total = w.reduce((a, x) => a + x, 0);
  let x = ctx.rng.next() * total;
  for (let i = 0; i < scores.length; i++) {
    x -= w[i] as number;
    if (x < 0) return (scores[i] as PlayScore).id;
  }
  return (scores.at(-1) as PlayScore).id;
}
